import { Cartesian3, Cartographic, Color, ConstantPositionProperty, Math as CMath, Ray, AxisAlignedBoundingBox, BoundingSphere, HeadingPitchRange, PolylineCollection, Material, } from "cesium";
import { EarthBrainUtils } from "../../utilities/earthbrainutility";
import { FlatGroundGeometry } from "./flatground-geometry";
import { DEFAULT_FLAT_GROUND_LABEL_COLOR, } from "./flatground-info-interface";
import { OBJECT_CELL_SIZE, } from "../../settings/earthbrain-site-settings";
import { HeightMap } from "../../core/height-map";
import { AssetType, CESIUM_PICKABLE_ENTITY, EmbankmentType, } from "../../types";
import { ConstructionState } from "../trench/earthbrain-trench";
import { Site } from "../site";
import { NEAR_ZERO_THRESHOLD } from "../../core/utils";
import { getRaycastableFromRubbingDestination } from "../../utilities/structure-utilities";
export var FlatGroundOperation;
(function (FlatGroundOperation) {
    FlatGroundOperation[FlatGroundOperation["CREATION"] = 0] = "CREATION";
    FlatGroundOperation[FlatGroundOperation["DRAG"] = 1] = "DRAG";
    FlatGroundOperation[FlatGroundOperation["FINISH"] = 2] = "FINISH";
})(FlatGroundOperation || (FlatGroundOperation = {}));
export class FlatGround {
    assetType = AssetType.Flat_Ground_Data;
    _flatGroundPointSphereMap;
    _pointsForFlatGround;
    _wgs84dataMemo;
    _id;
    _constructionState;
    _flatGroundGeometry;
    _labelsMap;
    _flatGroundSettings;
    _associatedTerrain;
    _rubbingDestination;
    _primitives;
    _heightMap;
    _flatGroundEditOutline;
    _polylineCollection;
    debugRenderedEntities;
    _viewer;
    _preview;
    // Visibility intent (source of truth). Primitives' `.show` mirror this.
    _isVisible = true;
    isVisible() {
        return this._isVisible;
    }
    get geometry() {
        return this._flatGroundGeometry.geometry;
    }
    constructor(viewer, preview, id, flatGroundSettings, rubbingDestination, associatedTerrain) {
        this._flatGroundPointSphereMap = new Map();
        this._pointsForFlatGround = [];
        this._id = id;
        this._viewer = viewer;
        this._preview = preview;
        this._flatGroundGeometry = new FlatGroundGeometry(this._id);
        this._labelsMap = new Map();
        this._flatGroundSettings = flatGroundSettings;
        this._associatedTerrain = associatedTerrain;
        this._rubbingDestination = rubbingDestination;
        this._primitives = [];
        this._polylineCollection = new PolylineCollection();
        viewer.scene.primitives.add(this._polylineCollection);
        this.debugRenderedEntities = [];
        this._constructionState = ConstructionState.NONE;
        this._wgs84dataMemo = { pathPoints: [], geometries: [] };
    }
    set constructionState(state) {
        this._constructionState = state;
    }
    get constructionState() {
        return this._constructionState;
    }
    get shelfSlopeVal() {
        const embankmentSlope = this._flatGroundSettings.embankmentSettings.shelfSlope;
        const cutSlope = this._flatGroundSettings.cutSettings.shelfSlope;
        const embankmentVal = Math.abs(embankmentSlope) < NEAR_ZERO_THRESHOLD
            ? 0
            : this._flatGroundSettings.embankmentSettings.shelfWidth /
                embankmentSlope;
        const cutVal = Math.abs(cutSlope) < NEAR_ZERO_THRESHOLD
            ? 0
            : this._flatGroundSettings.cutSettings.shelfWidth / cutSlope;
        return Math.min(embankmentVal, cutVal);
    }
    get id() {
        return this._id;
    }
    get points() {
        return this._pointsForFlatGround.map((p) => p.position);
    }
    get spheresKey() {
        return Array.from(this._flatGroundPointSphereMap.keys());
    }
    get settings() {
        return this._flatGroundSettings;
    }
    get heightMap() {
        return this._heightMap;
    }
    getSphereEntity(key) {
        const sphereEntity = Array.from(this._flatGroundPointSphereMap.values()).find((sphere) => sphere.id === key);
        return sphereEntity;
    }
    getSphereOnFlatGround(flatGroundPointID) {
        //get the sphere entity associated with the incoming point ID if present
        if (this._flatGroundPointSphereMap.has(flatGroundPointID)) {
            const sphereEntity = this._flatGroundPointSphereMap.get(flatGroundPointID);
            return sphereEntity;
        }
        return undefined;
    }
    get GroundPointsCount() {
        return this._pointsForFlatGround.length;
    }
    getInterpolatedPointsForPreview(points) {
        if (Array.isArray(points)) {
            return this._flatGroundGeometry.computeInterpolatedPointsOnPerimeter(points, this._flatGroundSettings.commonSettings.interpolationPitch);
        }
        else {
            return this._flatGroundGeometry.computeInterpolatedPointsOnPerimeter([...this._pointsForFlatGround.map((p) => p.position), points], this._flatGroundSettings.commonSettings.interpolationPitch);
        }
    }
    get associatedTerrain() {
        return this._associatedTerrain;
    }
    updateAssociatedTerrain(terrain) {
        this._associatedTerrain = terrain;
    }
    async updateAssociatedRubbingDestination(rubbingDestination) {
        this._rubbingDestination = rubbingDestination;
        await this.computeGeometry();
    }
    async _addPrimitives(show = true) {
        for (const wgs84geometry of this._wgs84dataMemo.geometries) {
            if (wgs84geometry.nonIndexedGeometry.vertices.length > 0 &&
                wgs84geometry.nonIndexedGeometry.vertices.length % 3 === 0) {
                const material = this.getColorBySegmentType(wgs84geometry.segmentType);
                const pickableType = this.getPickableTypeBySegmentType(wgs84geometry.segmentType);
                const primitive = EarthBrainUtils.createEntityFromIndexedGeometry(wgs84geometry.nonIndexedGeometry, material, {
                    pickInfo: {
                        entity: this,
                        assetType: AssetType.Flat_Ground_Data,
                        pickableType,
                        owner: this,
                        structureInfo: { id: this._id },
                    },
                });
                if (wgs84geometry.segmentType != EmbankmentType.NONE) {
                    const wireframePrimitive = EarthBrainUtils.createEntityFromIndexedGeometry(wgs84geometry.nonIndexedGeometry, Color.BLACK, {
                        pickInfo: {
                            entity: this,
                            assetType: AssetType.Flat_Ground_Data,
                            pickableType,
                            owner: this,
                            structureInfo: { id: this._id },
                        },
                    }, true);
                    wireframePrimitive.show = show;
                    this._viewer.scene.primitives.add(wireframePrimitive);
                    this._primitives.push(wireframePrimitive);
                }
                primitive.show = show;
                this._viewer.scene.primitives.add(primitive);
                this._primitives.push(primitive);
            }
        }
    }
    _disposePrimitives() {
        if (this._primitives.length > 0) {
            for (const primitive of this._primitives) {
                this._viewer.scene.primitives.remove(primitive);
            }
            this._primitives.length = 0;
        }
    }
    dispose() {
        this._disposePrimitives();
        for (const e of this.debugRenderedEntities) {
            this._viewer.entities.remove(e);
        }
        if (this._pointsForFlatGround.length > 0) {
            this._flatGroundGeometry?.dispose();
            this._pointsForFlatGround.length = 0;
            this._flatGroundPointSphereMap.forEach((point) => this._viewer.entities.remove(point));
            this._flatGroundPointSphereMap.clear();
            //Delete all labels
            this.removeAllLabels();
            this._id = undefined;
            if (this._polylineCollection) {
                this._viewer.scene.primitives.remove(this._polylineCollection);
                if (!this._polylineCollection.isDestroyed()) {
                    this._polylineCollection.removeAll();
                    this._polylineCollection.destroy();
                }
                this._polylineCollection = undefined;
            }
            this._flatGroundEditOutline = undefined;
            this._constructionState = ConstructionState.NONE;
        }
    }
    async appendPoint(point) {
        //If point is already present then don't append
        const site = Site.getInstance();
        if (this._pointsForFlatGround.find((position) => position.position.equals(point.position))) {
            throw new Error("ExistingPointAppendError");
        }
        this._pointsForFlatGround.push(point);
        if (this._pointsForFlatGround.length > 2) {
            if (this.constructionState !== ConstructionState.UNDER_CONSTRUCTION) {
                site.structureManager.reComputeConnectionNodesConnectedToChildStructure(this._id);
                await this.computeGeometry();
                //for children
                const structure = site.structureManager.getStructure(this._id);
                structure.updateConnectionOnAddUpdatePoint();
                site.structureManager.reComputeChildrenGeometry(this._id);
            }
        }
        //Create all labels
        const sphereEntity = this.getControlPointSphere(point.position);
        this._flatGroundPointSphereMap.set(sphereEntity.id, sphereEntity);
        this.reCreateLabels();
        this._preview.arrowPreviewForFlatArea(this, 10, 5);
        this._preview.arrowPreviewForFlatGroundCenter(this, 10, 5);
        return sphereEntity.id;
    }
    async appendPoints(points) {
        const site = Site.getInstance();
        const localPositions = await site.convertPointsToLocalSpace(points);
        points.forEach((point, i) => {
            if (this._pointsForFlatGround.find((position) => position.position.equals(point))) {
                throw new Error("ExistingPointAppendError");
            }
            this._pointsForFlatGround.push({
                position: point,
                localPosition: localPositions[i],
            });
        });
        if (this._pointsForFlatGround.length > 2) {
            await this.computeGeometry();
        }
        for (const point of points) {
            const sphereEntity = this.getControlPointSphere(point);
            this._flatGroundPointSphereMap.set(sphereEntity.id, sphereEntity);
        }
        //Create all labels
        this.reCreateLabels();
    }
    async removePoint(pointID) {
        const site = Site.getInstance();
        if (this._flatGroundPointSphereMap.has(pointID)) {
            if (this.constructionState !== ConstructionState.UNDER_CONSTRUCTION) {
                if (this._pointsForFlatGround.length < 3) {
                    throw new Error("pointRemoveError: ground needs atleast 3 points");
                }
            }
            const index = this.getFlatGroundPointIndex(pointID);
            // modify the map by deleting the point from map
            this._flatGroundPointSphereMap.delete(pointID);
            //modify the array, by removing the position of point
            this._pointsForFlatGround.splice(index, 1);
            //remove sphere from viewer with respect to id
            this._viewer.entities.removeById(pointID);
            if (this._pointsForFlatGround.length > 2) {
                if (this._constructionState !== ConstructionState.UNDER_CONSTRUCTION) {
                    site.structureManager.reComputeConnectionNodesConnectedToChildStructure(this._id);
                    await this.computeGeometry();
                    //for children
                    const structure = site.structureManager.getStructure(this._id);
                    structure.updateConnectionOnDeletePoint();
                    site.structureManager.reComputeChildrenGeometry(this._id);
                }
            }
            //Create all labels
            this.reCreateLabels();
        }
        this.updateOutline();
        this._preview.arrowPreviewForFlatArea(this, 10, 5);
        this._preview.arrowPreviewForFlatGroundCenter(this, 10, 5);
    }
    async appendPointAtIndex(index, pos) {
        const site = Site.getInstance();
        const arr = await site.convertPointsToLocalSpace([pos]);
        const localPos = arr[0];
        // create the new sphere entity and Modify the Map by pushing point at particular index
        const sphereEntity = this.getControlPointSphere(pos);
        if (index === this._flatGroundPointSphereMap.size) {
            //update the map
            //set sphereEntity at the end in map
            this._flatGroundPointSphereMap.set(sphereEntity.id, sphereEntity);
            //update the array
            //push position at the end in array
            this._pointsForFlatGround.push({
                position: pos,
                localPosition: localPos,
            });
        }
        else {
            //update the map
            EarthBrainUtils.insertKeyValuePairAtIndex(index, sphereEntity.id, sphereEntity, this._flatGroundPointSphereMap);
            //update the array
            //first add new cartesian at the index
            this._pointsForFlatGround.splice(index, 0, {
                position: pos,
                localPosition: localPos,
            });
        }
        //update the geometry
        this._pointsForFlatGround.length > 2 && (await this.computeGeometry());
        //Create all labels
        this.reCreateLabels();
        this.updateOutline();
        this._preview.arrowPreviewForFlatArea(this, 10, 5);
        this._preview.arrowPreviewForFlatGroundCenter(this, 10, 5);
        return sphereEntity.id;
    }
    getFlatGroundPointPos(index) {
        const pointPosition = this._pointsForFlatGround[index];
        return pointPosition.position;
    }
    getFlatGroundPointIndex(pointID) {
        const indexOfPoint = Array.from(this._flatGroundPointSphereMap.keys()).findIndex((sphereID) => sphereID === pointID);
        return indexOfPoint;
    }
    getControlPointPos(id) {
        if (id !== undefined) {
            const index = this.getFlatGroundPointIndex(id);
            return index !== undefined
                ? this._pointsForFlatGround[index].localPosition
                : undefined;
        }
        return undefined;
    }
    getCalcMaxControlPtHeight() {
        let highestPoint = this._pointsForFlatGround[0].localPosition;
        for (let i = 0; i < this._pointsForFlatGround.length; i++) {
            const point = this._pointsForFlatGround[i];
            const pointHeight = point.localPosition.z;
            const highestPointHeight = highestPoint.z;
            if (highestPointHeight < pointHeight) {
                highestPoint = point.localPosition;
            }
        }
        return highestPoint.z;
    }
    //TODO: height is local height and still implementation is not correct, need to fix it
    // local height should be converted to wgs84 height and applied to all wgs positions
    // This implementation though not correct, doesnot affect much.
    // Fix in different ticket when only local points will be used
    setUniformPointsHeight(height) {
        const maxLocalHeight = height !== undefined ? height : this.getCalcMaxControlPtHeight();
        const maxWGS84Height = height !== undefined
            ? height
            : Math.max(...this._pointsForFlatGround.map((c) => {
                const wgsPoint = Cartographic.fromCartesian(c.position);
                return wgsPoint.height;
            }));
        this._pointsForFlatGround.forEach((point, i) => {
            this._pointsForFlatGround[i].position = EarthBrainUtils.setAltitude(point.position, maxWGS84Height);
            this._pointsForFlatGround[i].localPosition.z = maxLocalHeight;
        });
    }
    updateSpheresPositionAndLabel() {
        Array.from(this._flatGroundPointSphereMap.values()).forEach((sphere, index) => {
            const pointPositionProperty = new ConstantPositionProperty(this._pointsForFlatGround[index].position);
            sphere.position = pointPositionProperty;
        });
        this.reCreateLabels();
    }
    async updateAllPointsPositions(newPositions) {
        const site = Site.getInstance();
        const localPositions = await site.convertPointsToLocalSpace(newPositions);
        this._pointsForFlatGround = newPositions.map((p, i) => ({
            position: p,
            localPosition: localPositions[i],
        }));
        site.structureManager.reComputeConnectionNodesConnectedToChildStructure(this._id);
        await this.computeGeometry();
        //for children
        const structure = site.structureManager.getStructure(this._id);
        structure.updateConnectionOnAddUpdatePoint();
        site.structureManager.reComputeChildrenGeometry(this._id);
        this.updateSpheresPositionAndLabel();
        this.updateOutline();
        this._preview.arrowPreviewForFlatArea(this, 10, 5);
        this._preview.arrowPreviewForFlatGroundCenter(this, 10, 5);
    }
    async updateGroundPointPosition(newPosition, clickedPointID, arrowEntity) {
        const site = Site.getInstance();
        //get the sphere and update the position
        const index = Array.from(this._flatGroundPointSphereMap.keys()).findIndex((PointID) => clickedPointID === PointID);
        if (arrowEntity) {
            this._arrowPreviewUpdate(arrowEntity, newPosition.position);
        }
        //update the array
        //first remove the index element
        const oldPoint = this._pointsForFlatGround.splice(index, 1);
        //add new cartesian at the index
        this._pointsForFlatGround.splice(index, 0, newPosition);
        site.structureManager.reComputeConnectionNodesConnectedToChildStructure(this._id);
        await this.computeGeometry();
        //for children
        const structure = site.structureManager.getStructure(this._id);
        structure.updateConnectionOnAddUpdatePoint();
        site.structureManager.reComputeChildrenGeometry(this._id);
        const pointPositionProperty = new ConstantPositionProperty(this._pointsForFlatGround[index].position);
        const sphereEntity = this._flatGroundPointSphereMap.get(clickedPointID);
        sphereEntity.position = pointPositionProperty;
        const labelEntity = this._labelsMap.get(clickedPointID);
        labelEntity.position = pointPositionProperty;
        this.reCreateLabels();
        this._preview.arrowPreviewForFlatArea(this, 10, 5);
        this._preview.arrowPreviewForFlatGroundCenter(this, 10, 5);
        //update the geometry
        // this._updateGeometry();
        this.updateOutline();
        return oldPoint[0];
    }
    getFlatGroundPointID(index) {
        const flatGroundPointIDArray = Array.from(this._flatGroundPointSphereMap.keys());
        return flatGroundPointIDArray[index];
    }
    _arrowPreviewUpdate(arrowEntity, startPos) {
        const startPosCarto = Cartographic.fromCartesian(startPos);
        const newPosCarto = Cartographic.fromRadians(startPosCarto.longitude, startPosCarto.latitude, startPosCarto.height + 5);
        const newPos = Cartographic.toCartesian(newPosCarto);
        const positions = [startPos, newPos];
        // @ts-ignore
        arrowEntity.polyline.positions = positions;
    }
    /**
     * Function to return flat ground mesh geometry.
     * @returns Mesh geometry of flat ground
     */
    getGeometry() {
        return this._flatGroundGeometry.geometry;
    }
    getCenterGeometry() {
        const flatGroundGeometry = this._flatGroundGeometry.createCenterGeometry();
        return flatGroundGeometry.nonIndexedGeometry;
    }
    //Todo save the settings also
    toJSON() {
        return {
            flatGroundAreaId: this.id,
            points: this._pointsForFlatGround.map((p) => ({
                position: [p.position.x, p.position.y, p.position.z],
            })),
            flatGroundSettings: this._flatGroundSettings,
            associatedTerrain: {
                id: this._associatedTerrain.id,
                terrainType: this._associatedTerrain.assetType,
            },
            rubbingDestination: this._rubbingDestination,
        };
    }
    // ToDo save the settings for flat groud also and then use it to create the object
    static async fromJSON(flatGroundData, cesiumViewer, preview) {
        const site = Site.getInstance();
        const pointsData = flatGroundData.points;
        const associatedTerrain = site.getTerrain(flatGroundData.associatedTerrain.id, flatGroundData.associatedTerrain.terrainType);
        const flatGroundObject = new FlatGround(cesiumViewer.viewer, preview, flatGroundData.flatGroundAreaId, flatGroundData.flatGroundSettings, flatGroundData.rubbingDestination, associatedTerrain);
        flatGroundObject.constructionState = ConstructionState.READY;
        // Append points
        const points = pointsData.map((element) => Cartesian3.fromArray(element.position));
        await flatGroundObject.appendPoints(points);
        return flatGroundObject;
    }
    fitToFlatGround() {
        if (this._wgs84dataMemo.pathPoints.length === 0)
            return;
        const boundingSphere = BoundingSphere.fromPoints(this._wgs84dataMemo.pathPoints);
        this._viewer.camera.flyToBoundingSphere(boundingSphere, {
            offset: new HeadingPitchRange(CMath.toRadians(0.0), CMath.toRadians(-30.0), 0.0),
        });
    }
    toggleFlatGroundVisibility(show) {
        this._isVisible = show;
        // Please update this function for flatGroundMesh
        // this._flatGroundGeometry.setVisibility(show);
        for (const e of this.debugRenderedEntities) {
            e.show = show;
        }
        for (const primitive of this._primitives) {
            primitive.show = show;
        }
        this._labelsMap.forEach((value) => {
            value.show = show;
        });
        this._flatGroundPointSphereMap.forEach((value) => {
            value.show = show;
        });
    }
    reCreateLabels(color = DEFAULT_FLAT_GROUND_LABEL_COLOR) {
        const keyArray = this.spheresKey;
        //removing all labels
        this.removeAllLabels();
        this._pointsForFlatGround.forEach((position, index) => {
            const label = EarthBrainUtils.addLabel(this._viewer, position.position, index.toString(), {
                fillColor: Color.fromCssColorString(color),
            });
            const entity = this.getSphereEntity(keyArray[index]);
            this._labelsMap.set(entity.id, label);
        });
    }
    removeAllLabels() {
        this._labelsMap.forEach((label) => this._viewer.entities.remove(label));
        this._labelsMap.clear();
    }
    updateLabelColors(color) {
        this.reCreateLabels(color);
    }
    _updateFlatGroundEditOutline() {
        if (this._flatGroundEditOutline) {
            this._polylineCollection.remove(this._flatGroundEditOutline);
        }
        const positions = this._wgs84dataMemo.pathPoints;
        if (positions.length > 2) {
            this._flatGroundEditOutline = this._polylineCollection.add({
                name: CESIUM_PICKABLE_ENTITY.FLAT_GROUND_EDIT_OUTLINE,
                positions: positions,
                width: 10,
            });
            this._flatGroundEditOutline.material = Material.fromType("PolylineOutline", {
                color: Color.BLUE.withAlpha(0.001),
                outlineColor: Color.BLUE.withAlpha(0.001),
            });
            this._flatGroundEditOutline.show = false;
            this._flatGroundEditOutline.id = {
                pickInfo: {
                    assetType: AssetType.Flat_Ground_Data,
                    pickableType: CESIUM_PICKABLE_ENTITY.FLAT_GROUND_EDIT_OUTLINE,
                    entity: this._flatGroundEditOutline,
                    owner: this,
                },
            };
        }
    }
    _updatePolylineCollection() {
        this._polylineCollection.removeAll();
        this._updateFlatGroundEditOutline();
    }
    async syncLabelAndSpherePosition(site) {
        const keyArray = this.spheresKey;
        //removing all labels
        for (let index = 0; index < this._pointsForFlatGround.length; ++index) {
            const position = this._pointsForFlatGround[index];
            const [wgsPoint] = await site.convertPointsToWGS84([
                position.localPosition,
            ]);
            if (wgsPoint) {
                wgsPoint.clone(position.position);
                const entity = this.getSphereEntity(keyArray[index]);
                const label = this._labelsMap.get(entity?.id);
                if (entity && label) {
                    entity.position = new ConstantPositionProperty(wgsPoint);
                    label.position = new ConstantPositionProperty(wgsPoint);
                }
            }
        }
    }
    async _updateWGS84Data(site) {
        const positions = this._pointsForFlatGround.map((p) => p.localPosition);
        this._wgs84dataMemo.pathPoints = await site.convertPointsToWGS84(positions);
        const flatGroundSubGeometries = this._flatGroundGeometry.getSubGeometries();
        for (const data of flatGroundSubGeometries) {
            try {
                const resp = await site.convertPointsToWGS84(Cartesian3.unpackArray(data.nonIndexedGeometry.vertices));
                if (resp) {
                    data.nonIndexedGeometry.vertices = Cartesian3.packArray(resp);
                }
            }
            catch (error) {
                console.warn(error);
                data.nonIndexedGeometry.vertices.length = 0;
            }
        }
        this._wgs84dataMemo.geometries = flatGroundSubGeometries;
    }
    async computeGeometry() {
        const site = Site.getInstance();
        for (const e of this.debugRenderedEntities) {
            this._viewer.entities.remove(e);
        }
        //check if it depends on others -> add those dependents as intersection data
        const parentNodes = site.structureManager.getParentsNodes(this._id);
        for (const parentNode of parentNodes) {
            const parentStructure = site.structureManager.getStructure(parentNode.from.id);
            const pointInfo = parentStructure.getPointInfoAtProportionIntotal(parentNode.from.proportionInTotal);
            pointInfo.pointOnPath.clone(this._pointsForFlatGround[parentNode.to.controlPointIndex]
                .localPosition);
        }
        const rayCastCapable = getRaycastableFromRubbingDestination(site, this._rubbingDestination);
        this._flatGroundGeometry.updateGeometry(this._pointsForFlatGround.map((p) => p.localPosition), this._flatGroundSettings, rayCastCapable);
        await this._updateWGS84Data(site);
        const shouldShow = this._isVisible;
        this._disposePrimitives();
        await this._addPrimitives(shouldShow);
        if (!shouldShow) {
            this.toggleFlatGroundVisibility(false);
        }
        await this.syncLabelAndSpherePosition(site);
        this._heightMap = undefined;
        const geom = this.getGeometry();
        if (geom.vertices.length > 0) {
            this._heightMap = HeightMap.FromIndexedGeometry(geom, OBJECT_CELL_SIZE);
        }
    }
    async updateSettings(settings) {
        this._flatGroundSettings = settings;
        await this.computeGeometry();
    }
    computeCentralGeometry() {
        this._flatGroundGeometry.updateCentralGeometry(this._pointsForFlatGround.map((p) => p.localPosition));
    }
    async updateMaterial(commonMaterial, embankmentMaterial, cutMaterial) {
        this._flatGroundSettings.commonSettings.transparency =
            commonMaterial.commonSettings.transparency;
        this._flatGroundSettings.commonSettings.surfaceColor =
            commonMaterial.commonSettings.surfaceColor;
        if (embankmentMaterial.material) {
            this._flatGroundSettings.embankmentSettings.material =
                embankmentMaterial.material;
        }
        if (embankmentMaterial.transparency) {
            this._flatGroundSettings.embankmentSettings.transparency =
                embankmentMaterial.transparency;
        }
        if (cutMaterial.material) {
            this._flatGroundSettings.cutSettings.material = cutMaterial.material;
        }
        if (cutMaterial.transparency) {
            this._flatGroundSettings.cutSettings.transparency =
                cutMaterial.transparency;
        }
        if (this.isVisible()) {
            this._disposePrimitives();
            await this._addPrimitives();
        }
    }
    rayCast(ray) {
        return EarthBrainUtils.intersectAgainstIGeometry(ray, this.getGeometry());
    }
    getProjectPointHeight(refPoint) {
        const ray = new Ray(new Cartesian3(refPoint.x, refPoint.y, refPoint.z + 10000), new Cartesian3(0, 0, -1));
        const pointOnTileset = this.rayCast(ray);
        return pointOnTileset !== undefined ? pointOnTileset.z : undefined;
    }
    getClosestPointInfoOnPath(point) {
        const controlPoints = this._pointsForFlatGround.map((p) => p.localPosition);
        const { segmentIndex } = EarthBrainUtils.getIndexForProbableSegmentAndProjectedPoint(point, controlPoints);
        const nextIndex = (segmentIndex + 1) % controlPoints.length;
        //point lies between subSegmentIndex && subSegmentIndex + 1
        const pointVector = Cartesian3.subtract(controlPoints[segmentIndex], point, new Cartesian3());
        const pathVector = Cartesian3.subtract(controlPoints[segmentIndex], controlPoints[nextIndex], new Cartesian3());
        const projectedVector = Cartesian3.projectVector(pointVector, pathVector, new Cartesian3());
        const result = Cartesian3.add(controlPoints[segmentIndex], projectedVector, new Cartesian3());
        let distance = 0;
        for (let i = 0; i < segmentIndex; ++i) {
            const nextIndex = (i + 1) % controlPoints.length;
            distance += Cartesian3.distance(controlPoints[i], controlPoints[nextIndex]);
        }
        const distanceInSegment = Cartesian3.distance(controlPoints[segmentIndex], result);
        distance += distanceInSegment;
        return {
            point: result,
            distanceFromStart: distance,
            distanceInSegment: distanceInSegment,
            segmentBound: [
                controlPoints[segmentIndex],
                controlPoints[(segmentIndex + 1) % controlPoints.length],
            ],
        };
    }
    getPerimeter() {
        let totalLength = 0;
        const points = this._pointsForFlatGround.map((p) => p.localPosition);
        for (let i = 0; i < points.length; ++i) {
            const nextIndex = (i + 1) % points.length;
            totalLength += Cartesian3.distance(points[i], points[nextIndex]);
        }
        return totalLength;
    }
    getPointInfoAtDistance(distanceFromStart) {
        const retVal = { pointOnPath: undefined, bounds: [], segmentId: undefined };
        const totalLength = this.getPerimeter();
        if (distanceFromStart > totalLength) {
            return retVal;
        }
        let distanceComputed = 0;
        for (let i = 0; i < this._pointsForFlatGround.length; ++i) {
            const nextIndex = (i + 1) % this._pointsForFlatGround.length;
            const segDistance = Cartesian3.distance(this._pointsForFlatGround[i].localPosition, this._pointsForFlatGround[nextIndex].localPosition);
            distanceComputed += segDistance;
            if (distanceComputed >= distanceFromStart) {
                const distanceForSegment = distanceFromStart - distanceComputed + segDistance;
                const dir = EarthBrainUtils.getDirBetweenTwoCartesians(this._pointsForFlatGround[i].localPosition, this._pointsForFlatGround[nextIndex].localPosition);
                const pointOnPath = Cartesian3.add(this._pointsForFlatGround[i].localPosition, Cartesian3.multiplyByScalar(dir, distanceForSegment, new Cartesian3()), new Cartesian3());
                const bounds = [
                    this._pointsForFlatGround[i].localPosition,
                    this._pointsForFlatGround[nextIndex].localPosition,
                ];
                retVal.segmentId = i;
                retVal.pointOnPath = pointOnPath;
                retVal.bounds = bounds;
                break;
            }
        }
        return retVal;
    }
    getColorBySegmentType(segType) {
        switch (segType) {
            case EmbankmentType.FILL:
                const embankmentMaterial = Color.fromCssColorString(this._flatGroundSettings.embankmentSettings.material);
                embankmentMaterial.alpha =
                    this._flatGroundSettings.embankmentSettings.transparency;
                return embankmentMaterial;
            case EmbankmentType.CUT:
                const cutMaterial = Color.fromCssColorString(this._flatGroundSettings.cutSettings.material);
                cutMaterial.alpha = this._flatGroundSettings.cutSettings.transparency;
                return cutMaterial;
            case EmbankmentType.NONE:
                const flatGroundSurfaceMaterial = Color.fromCssColorString(this._flatGroundSettings.commonSettings.surfaceColor);
                flatGroundSurfaceMaterial.alpha =
                    this._flatGroundSettings.commonSettings.transparency;
                return flatGroundSurfaceMaterial;
        }
    }
    getPickableTypeBySegmentType(segType) {
        switch (segType) {
            case EmbankmentType.FILL:
                return CESIUM_PICKABLE_ENTITY.FLAT_GROUND_EMBARKMENT_GEOMETRY;
            case EmbankmentType.CUT:
                return CESIUM_PICKABLE_ENTITY.FLAT_GROUND_CUT_GEOMETRY;
            case EmbankmentType.NONE:
                return CESIUM_PICKABLE_ENTITY.FLAT_GROUND_GEOMETRY;
        }
    }
    getControlPointSphere(position) {
        const sphereEntity = EarthBrainUtils.addSphere(this._viewer, position, Color.GREY, Number.POSITIVE_INFINITY);
        sphereEntity.addProperty("pickInfo"); //Information when this sphere gets picked
        sphereEntity["pickInfo"] = {
            assetType: AssetType.Flat_Ground_Data,
            pickableType: CESIUM_PICKABLE_ENTITY.FLAT_GROUND_CONTROL_POINT,
            entity: sphereEntity,
            owner: this,
        };
        return sphereEntity;
    }
    updateOutline() {
        if (this._pointsForFlatGround.length > 0) {
            const positions = this._pointsForFlatGround.map((p) => p.position);
            positions.push(this._pointsForFlatGround[0].position);
            this._wgs84dataMemo.pathPoints = positions;
            this._updatePolylineCollection();
            this._flatGroundEditOutline.show = true;
        }
    }
    disableForEdit() {
        this._flatGroundEditOutline.show = false;
    }
    containsPointWithID(pointId) {
        return this.spheresKey.includes(pointId);
    }
    computeSurfaceArea(flatgroundCenterGeom) {
        const surfaceArea = this._flatGroundGeometry.computeSurfaceArea(flatgroundCenterGeom);
        return surfaceArea;
    }
    tracePoints(points) {
        return EarthBrainUtils.tracePointsOnGeometry(this._flatGroundGeometry.geometry, this.heightMap, points);
    }
    /**
     * Calculates and returns the axis-aligned bounding box (AABB) for the flat ground.
     * The AABB is computed based on the WGS84 coordinates of the control points and geometry vertices.
     * If no points are available, an empty AABB is returned.
     *
     * NOTE: using heighmap AABB or chaching AABB will enhance performance.
     *
     * @returns {AxisAlignedBoundingBox} The AABB of the flat ground.
     */
    flatGroundAABB_WGS84() {
        const controlPointsWGS84 = this._wgs84dataMemo.pathPoints;
        const geometryVerticesWGS84 = [];
        for (const geomData of this._wgs84dataMemo.geometries) {
            if (geomData.nonIndexedGeometry.vertices.length > 0) {
                const unpackedVertices = Cartesian3.unpackArray(geomData.nonIndexedGeometry.vertices);
                geometryVerticesWGS84.push(...unpackedVertices);
            }
        }
        const allWGS84Points = controlPointsWGS84.concat(geometryVerticesWGS84);
        if (allWGS84Points.length === 0) {
            return new AxisAlignedBoundingBox();
        }
        return AxisAlignedBoundingBox.fromPoints(allWGS84Points);
    }
}
//# sourceMappingURL=earthbrain-flatground.js.map